package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.data.ScanHistoryDiff
import org.zq.astrock_ai.ui.research.gushi.data.ScanHistorySnapshot
import org.zq.astrock_ai.ui.research.gushi.data.chainScanHistoryDiffs
import org.zq.astrock_ai.ui.research.gushi.data.exportScanDiffText
import org.zq.astrock_ai.ui.research.gushi.data.formatScanHistoryTime
import org.zq.astrock_ai.ui.research.gushi.data.scanChangeKindFromName

@Composable
fun ScanHistoryDialog(
    entries: List<ScanHistorySnapshot>,
    onDismiss: () -> Unit,
    onSelectCode: (String) -> Unit = {},
    onExportDiff: ((String) -> Unit)? = null,
) {
    val shape = RoundedCornerShape(14.dp)
    val chainDiffs = remember(entries) { chainScanHistoryDiffs(entries, maxPairs = 4) }
    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(440.dp)
                .heightIn(max = 580.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(18.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "扫描历史",
                    color = StockColors.TextPrimary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.weight(1f),
                )
                if (onExportDiff != null && chainDiffs.isNotEmpty()) {
                    Text(
                        "导出差异",
                        color = StockColors.PrimaryBlue,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier
                            .background(Color(0xFFE8F0FE), RoundedCornerShape(10.dp))
                            .clickable {
                                onExportDiff(exportScanDiffText(chainDiffs))
                            }
                            .padding(horizontal = 10.dp, vertical = 5.dp),
                    )
                    Spacer(Modifier.width(8.dp))
                }
                Text(
                    "关闭",
                    color = StockColors.PrimaryBlue,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .background(Color(0xFFE8F0FE), RoundedCornerShape(12.dp))
                        .clickable(onClick = onDismiss)
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                )
            }
            Spacer(Modifier.height(6.dp))
            Text(
                "点击标的可跳转；顶部为相邻期链式差异（最多 4 段）",
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
            Spacer(Modifier.height(12.dp))

            if (entries.isEmpty()) {
                Text("暂无历史，先点「共识扫描」", color = StockColors.TextSecondary, fontSize = 13.sp)
            } else {
                LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (chainDiffs.isNotEmpty()) {
                        item(key = "chain-header") {
                            Text(
                                "多期差异链 · ${chainDiffs.size} 段",
                                color = StockColors.PrimaryBlue,
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 13.sp,
                            )
                        }
                        items(chainDiffs, key = { "${it.newerAtMs}-${it.olderAtMs}" }) { diff ->
                            TimelineDiffCard(diff = diff, onSelectCode = onSelectCode)
                        }
                    }
                    item(key = "entries-header") {
                        Text(
                            "全部快照 · ${entries.size}",
                            color = StockColors.TextSecondary,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 12.sp,
                            modifier = Modifier.padding(top = 4.dp),
                        )
                    }
                    items(entries, key = { it.atMs }) { entry ->
                        HistoryEntryCard(entry = entry, onSelectCode = onSelectCode)
                    }
                }
            }
        }
    }
}

@Composable
private fun TimelineDiffCard(
    diff: ScanHistoryDiff,
    onSelectCode: (String) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFEEF4FF), RoundedCornerShape(10.dp))
            .padding(12.dp),
    ) {
        Text(
            "${formatScanHistoryTime(diff.newerAtMs)} ← ${formatScanHistoryTime(diff.olderAtMs)}",
            color = StockColors.PrimaryBlue,
            fontWeight = FontWeight.SemiBold,
            fontSize = 12.sp,
        )
        Spacer(Modifier.height(4.dp))
        Text(
            if (diff.totalChanges == 0) "信号集合无变化" else "共 ${diff.totalChanges} 项变化",
            color = StockColors.TextSecondary,
            fontSize = 11.sp,
        )
        DiffLine("新现买", diff.appearedBuy, StockColors.UpRed, onSelectCode)
        DiffLine("新现卖", diff.appearedSell, StockColors.DownGreen, onSelectCode)
        DiffLine("转买", diff.flippedToBuy, StockColors.UpRed, onSelectCode)
        DiffLine("转卖", diff.flippedToSell, StockColors.DownGreen, onSelectCode)
        DiffLine("信号消失", diff.disappeared, StockColors.Neutral, onSelectCode)
    }
}

@Composable
private fun DiffLine(
    label: String,
    codes: List<String>,
    color: Color,
    onSelectCode: (String) -> Unit,
) {
    if (codes.isEmpty()) return
    Spacer(Modifier.height(4.dp))
    Text("$label ${codes.size}", color = color, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
    codes.take(8).forEach { code ->
        Text(
            code,
            color = color,
            fontSize = 12.sp,
            modifier = Modifier
                .fillMaxWidth()
                .clickable { onSelectCode(code) }
                .padding(vertical = 1.dp),
        )
    }
    if (codes.size > 8) {
        Text("…另有 ${codes.size - 8} 只", color = StockColors.Neutral, fontSize = 11.sp)
    }
}

@Composable
private fun HistoryEntryCard(
    entry: ScanHistorySnapshot,
    onSelectCode: (String) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                formatScanHistoryTime(entry.atMs),
                color = StockColors.TextPrimary,
                fontWeight = FontWeight.SemiBold,
                fontSize = 13.sp,
                modifier = Modifier.weight(1f),
            )
            Text(
                "买${entry.buyCount} 卖${entry.sellCount} 观望${entry.holdCount}",
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
        }
        if (entry.changeCount > 0) {
            Spacer(Modifier.height(6.dp))
            Text(
                "变化 ${entry.changeCount}",
                color = StockColors.PrimaryBlue,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            entry.changes.take(6).forEach { change ->
                val kind = scanChangeKindFromName(change.kind)
                val label = kind?.label ?: change.kind
                val color = when (kind?.name) {
                    "NEW_BUY", "TO_BUY" -> StockColors.UpRed
                    "NEW_SELL", "TO_SELL" -> StockColors.DownGreen
                    else -> StockColors.TextSecondary
                }
                Text(
                    "${change.name.ifBlank { change.code }} · $label",
                    color = color,
                    fontSize = 12.sp,
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSelectCode(change.code) }
                        .padding(vertical = 2.dp),
                )
            }
            if (entry.changes.size > 6) {
                Text("…另有 ${entry.changes.size - 6} 项", color = StockColors.Neutral, fontSize = 11.sp)
            }
        } else {
            Spacer(Modifier.height(4.dp))
            entry.highlights.take(4).forEach { item ->
                val tag = when (item.signal) {
                    "BUY" -> "买"
                    "SELL" -> "卖"
                    else -> item.signal
                }
                Text(
                    "${item.name.ifBlank { item.code }} · $tag",
                    color = StockColors.TextSecondary,
                    fontSize = 12.sp,
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSelectCode(item.code) }
                        .padding(vertical = 2.dp),
                )
            }
            if (entry.highlights.isEmpty()) {
                Text("无买卖信号", color = StockColors.TextSecondary, fontSize = 12.sp)
            }
        }
    }
}
